import { WALL_EPSILON, wallDistance, wallIntersectsCylinder, type PaintedWallGeometry, type WallPoint } from '../../../shared/painted-wall-geometry';
import { encounterField } from '../../../shared/array-types';
import { ecId, handBack, type JanusCtx } from '../context';
import { resolveForcedMovement } from '../../movement/forced-movement';
import { encounterShapeOf, forcedMovementReaders, landRow } from '../movement/check-push-and-pull-after-hit';
import { OPEN_PLANE } from '../../movement/shapeless-prop-snap';
import { withExteriorOpenGround } from '../../inventory/lift-occupancy';
import type { RestShape } from '../../inventory/floor-move';
import { paintedWallIntersectsTarget, paintedWallTargetVolume } from './painted-wall-cast';
import type { PlacedAoeReaders } from './expand-placed-aoe-targets';
import { iceWallDisplacements } from './painted-wall-ice-displacement-state';

/** No legacy displacement existed. Settle at the nearest legal side with minimum full-body clearance;
 * ordinary contest/damage dispatch still owns the authored Balance save and 10d6 Cold (half on success). */
export function prepareIceWallDisplacements(ctx: JanusCtx, wall: PaintedWallGeometry, placed: PlacedAoeReaders): void {
  iceWallDisplacements.delete(ctx);
  const occupants = [...ctx.members.values()].filter(row => ecId(row) && paintedWallIntersectsTarget(placed, wall, row));
  if (!occupants.length) return;
  const read = forcedMovementReaders(ctx), planned = new Map<string, WallPoint>();
  const position = read.position;
  read.position = row => {
    const held = planned.get(ecId(row));
    return held ? [held.x, held.y, held.z] : position(row);
  };
  // A full-radius destination keeps the whole body out of the newly formed solid ribbon.
  read.allowSqueeze = false;
  const heldShape = read.runtimeShape ?? encounterShapeOf(ctx);
  const storedShape = encounterField(ctx.encounter, 'encounter_shape');
  if (!heldShape && storedShape != null && storedShape !== '') handBack('ft_painted_wall_ice_cast_sections:scene-shape-not-held');
  const exterior = Number(encounterField(ctx.encounter, 'encounter_inside') ?? 0) !== 1;
  for (const row of occupants) {
    const [x, y, z] = read.position(row), base = read.baseSizeFt(row), height = read.heightFt(row);
    if (![x, y, z].every(Number.isFinite) || base === null || height === null)
      handBack('ft_painted_wall_ice_cast_sections:body-not-held');
    if (['riding', 'ridden', 'piloting', 'piloted', 'grappling', 'grappled'].some(key => read.hasStatus(row, key)))
      handBack('ft_painted_wall_ice_cast_sections:compound-displacement-unported');
    const volume = paintedWallTargetVolume(placed, row)!;
    const start = { x, y, z }, radius = Math.max(base! / 2, volume.r ?? Math.max(volume.halfW ?? 0, volume.halfD ?? 0));
    const clearance = radius + wall.thicknessFt / 2 + 2 * WALL_EPSILON;
    const candidates: WallPoint[] = [];
    for (let i = 1; i < wall.points.length; i++) {
      const a = wall.points[i - 1]!, b = wall.points[i]!, dx = b.x - a.x, dy = b.y - a.y, length = Math.hypot(dx, dy);
      if (!wallIntersectsCylinder({ ...wall, points: [a, b] }, start, radius, height!)) continue;
      const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (y - a.y) * dy) / (length * length)));
      if (z > a.z + (b.z - a.z) * t + wall.heightFt || z + height! < a.z + (b.z - a.z) * t) continue;
      const center = { x: a.x + dx * t, y: a.y + dy * t };
      for (const side of [-1, 1]) candidates.push({ x: center.x - side * dy / length * clearance,
        y: center.y + side * dx / length * clearance, z });
    }
    candidates.sort((a, b) => wallDistance(start, a) - wallDistance(start, b) || a.x - b.x || a.y - b.y);
    let end: WallPoint | undefined, unresolved: string | undefined;
    for (const candidate of candidates) {
      const shape = heldShape ? withExteriorOpenGround(heldShape as unknown as RestShape, x, y, candidate.x, candidate.y, 40 + base!, exterior)
        : OPEN_PLANE as unknown as RestShape;
      // Keep authored overlays marked unknown; only a scene-projected shape proves their current state.
      const movementRead = { ...read, runtimeShape: read.runtimeShape ? shape : null };
      const result = resolveForcedMovement({ encounter: ctx.encounter, shape, caster: row, target: row,
        reference: { x: 2 * x - candidate.x, y: 2 * y - candidate.y, z },
        push: wallDistance(start, candidate), pull: 0, toCasterCenter: false }, movementRead);
      if (result.kind === 'handback') { unresolved ??= 'ft_painted_wall_ice_cast_sections:landing-' + result.reason; continue; }
      if (result.kind !== 'move' || result.inventoryMove || result.dragged.length || result.collision?.dice) continue;
      if (result.zoneReconcile) handBack('ft_painted_wall_ice_cast_sections:existing-zone-displacement-unported');
      const at = { x: Number(result.end.x.toFixed(3)), y: Number(result.end.y.toFixed(3)), z: Number(result.end.z.toFixed(3)) };
      if (wallIntersectsCylinder(wall, at, radius, height!)) continue;
      const fall = result.fall;
      if (fall.kind !== 'none') {
        unresolved ??= 'ft_painted_wall_ice_cast_sections:landing-' + (fall.kind === 'handback' ? fall.reason : 'fall-unported');
        continue;
      }
      if (fall.snapZ !== undefined) at.z = fall.snapZ;
      if (wallIntersectsCylinder(wall, at, radius, height!)) continue;
      end = at;
      break;
    }
    if (!end) handBack(unresolved ?? 'ft_painted_wall_ice_cast_sections:no-legal-landing');
    planned.set(ecId(row), end!);
  }
  iceWallDisplacements.set(ctx, { moves: [...planned].map(([id, end]) => ({ id, end })), applied: false, land: landRow });
}
